import { Vulnerability, PaginationInfo, VulnerabilityBannerResponse, RemediationItemProgress, RemediationPlanDraft } from '../Utils/types';

const getVulnerabilityRowKey = (
  item: {
    softwareAssetInventoryId?: string;
    cveId?: string;
    packageIdentifier?: string;
    version?: string;
    title?: string;
    cveUrl?: string;
  },
  source: string,
  page: number,
  index: number,
) => {
  const compositeKey = [source, item.softwareAssetInventoryId, item.cveId, item.packageIdentifier, item.version, item.title, item.cveUrl]
    .filter(Boolean)
    .join('::');

  return compositeKey || `${source}::page-${page}::row-${index}`;
};

const getAgeInDays = (publishedDateValue?: string) => {
  if (!publishedDateValue) return 0;
  const publishedDate = new Date(publishedDateValue);
  if (Number.isNaN(publishedDate.getTime())) return 0;
  const today = new Date();
  return Math.floor((today.getTime() - publishedDate.getTime()) / (1000 * 60 * 60 * 24));
};

export async function getPatchifiBanner(
  selectedTenantId: string,
  getVulnerabilityBanner: (tenantId: string) => Promise<VulnerabilityBannerResponse>,
) {
  const response = await getVulnerabilityBanner(selectedTenantId);
  return response;
}

export async function getPatchifiVulnerabilities(params: {
  selectedTenantId: string;
  page: number;
  itemsPerPage: number;
  selectedSeverity: string;
  selectedKev: string;
  selectedEpss: string;
  searchTerm: string;
  getSoftwareAssets: (params: any) => Promise<any>;
}) {
  let kevExploited: boolean | undefined = undefined;
  if (params.selectedKev === 'Yes') kevExploited = true;
  if (params.selectedKev === 'No') kevExploited = false;

  let epssThreshold: number | undefined = undefined;
  if (params.selectedEpss === '>0.5') epssThreshold = 0.5;
  if (params.selectedEpss === '>0.7') epssThreshold = 0.7;

  const response = await params.getSoftwareAssets({
    tenantId: params.selectedTenantId,
    isSupported: true,
    pageNumber: params.page,
    pageSize: params.itemsPerPage,
    severity: params.selectedSeverity || undefined,
    kevExploited,
    epssThreshold,
    search: params.searchTerm || undefined,
  });

  if (response?.isSuccess && response?.data) {
    const mappedData: Vulnerability[] = response.data.map((item: any, index: number) => {
      return {
        rowKey: getVulnerabilityRowKey(
          {
            softwareAssetInventoryId: item.softwareAssetInventoryId,
            cveId: item.cveId,
            packageIdentifier: item.packageIdentifier,
            version: item.version,
            title: item.name,
            cveUrl: item.cveUrl,
          },
          'Patchifi',
          params.page,
          index,
        ),
        softwareAssetInventoryId: item.softwareAssetInventoryId,
        cveId: item.cveId || 'N/A',
        cveUrl: item.cveUrl,
        title: item.name,
        severity: item.baseSeverity || 'Unknown',
        cvssScore: item.cvss ? parseFloat(item.cvss) : 0,
        epssScore: item.epssScore ?? 0,
        kev: item.isKEV,
        affectedDevicesCount: item.installedEndpoints + item.installedServers,
        ageInDays: getAgeInDays(item.cveDatePublished),
        description: item.descriptions,
        version: item.version,
        packageIdentifier: item.packageIdentifier,
      };
    });

    return {
      vulnerabilities: mappedData,
      paginationInfo: response.pagination || {
        pageNumber: params.page,
        pageSize: params.itemsPerPage,
        totalRecords: mappedData.length,
        totalPages: 1,
        hasPreviousPage: false,
        hasNextPage: false,
      },
    };
  } else {
    return {
      vulnerabilities: [],
      paginationInfo: null,
    };
  }
}

export const pluralizeDevices = (count: number) => `${count} device${count !== 1 ? 's' : ''}`;

export const buildRemediationPlanName = (softwareName: string, date: Date = new Date()) =>
  `${softwareName} Vulnerability Update - ${date.toLocaleDateString()}`;

// Works out which plans the selection needs, without creating anything, so the user can review
// them first. Each table row is one package at one version, so rows for the same package are
// merged: one plan per software, on only the devices that have an outdated version of it.
export async function prepareRemediationPlans(params: {
  vulnerabilities: Vulnerability[];
  selectedTenantId: string;
  getSoftwareAssetSummary: (tenantId: string, id: string) => Promise<any>;
  getSoftwareAssetDevices: (tenantId: string, id: string) => Promise<any>;
  onProgress?: (update: RemediationItemProgress) => void;
  shouldStop?: () => boolean;
}): Promise<RemediationPlanDraft[]> {
  const drafts = new Map<string, RemediationPlanDraft>();

  for (const vulnerability of params.vulnerabilities) {
    if (params.shouldStop?.()) break;

    const key = vulnerability.rowKey;
    const title = vulnerability.title || vulnerability.cveId || 'Unknown vulnerability';
    const cveId = vulnerability.cveId;

    try {
      params.onProgress?.({ key, title, cveId, stage: 'processing', message: 'Looking up the latest version…' });

      const summaryResponse = await params.getSoftwareAssetSummary(params.selectedTenantId, vulnerability.softwareAssetInventoryId);
      const summary = summaryResponse?.isSuccess ? summaryResponse?.data : null;
      const packageIdentifier = summary?.packageIdentifier || vulnerability.packageIdentifier;
      const currentVersion = summary?.softwareAssetVersion;
      const latestVersion = summary?.latestPackageVersion;

      if (!packageIdentifier || !currentVersion || !latestVersion) {
        params.onProgress?.({ key, title, cveId, stage: 'skipped', message: 'Version details not available' });
        continue;
      }

      if (currentVersion === latestVersion) {
        params.onProgress?.({ key, title, cveId, stage: 'skipped', message: 'Already up to date' });
        continue;
      }

      params.onProgress?.({ key, title, cveId, stage: 'processing', message: 'Finding affected devices…' });

      const devicesResponse = await params.getSoftwareAssetDevices(params.selectedTenantId, vulnerability.softwareAssetInventoryId);
      const rowDeviceIds: string[] = devicesResponse?.isSuccess
        ? (devicesResponse?.data?.assetByAgentDetails ?? []).map((device: any) => device.tenantAgentID).filter(Boolean)
        : [];

      if (rowDeviceIds.length === 0) {
        params.onProgress?.({ key, title, cveId, stage: 'skipped', message: 'No affected devices found' });
        continue;
      }

      const softwareName = summary.softwareAssetName || vulnerability.title || packageIdentifier;
      const draft = drafts.get(packageIdentifier) ?? {
        key: packageIdentifier,
        softwareName,
        packageIdentifier,
        currentVersions: [],
        targetVersion: latestVersion,
        deviceIds: [],
        cveIds: [],
        planName: buildRemediationPlanName(softwareName),
      };

      draft.currentVersions = Array.from(new Set([...draft.currentVersions, currentVersion]));
      draft.deviceIds = Array.from(new Set([...draft.deviceIds, ...rowDeviceIds]));
      if (cveId && cveId !== 'N/A') draft.cveIds = Array.from(new Set([...draft.cveIds, cveId]));
      drafts.set(packageIdentifier, draft);

      params.onProgress?.({ key, title, cveId, stage: 'success', message: `${pluralizeDevices(rowDeviceIds.length)} to update` });
    } catch (error) {
      console.error(`Error preparing remediation for ${vulnerability.cveId}:`, error);
      params.onProgress?.({ key, title, cveId, stage: 'failed', message: 'Could not check this software' });
    }
  }

  return Array.from(drafts.values());
}

// An OS update needs only its KB. createRemediationPlan moves a KB into WindowsOSUpdateList when it
// is sent as PackageIdentifier; 'Updates' is the category every OS plan in the app uses.
const toPlanPatch = (draft: RemediationPlanDraft) =>
  draft.kbId
    ? { PackageIdentifier: draft.kbId, category: 'Updates' }
    : { packageIdentifier: draft.packageIdentifier, targetVersion: draft.targetVersion };

// Creates the reviewed plans one at a time, so one failed plan doesn't stop the others.
export async function createRemediationPlans(params: {
  drafts: RemediationPlanDraft[];
  tenantId: string;
  isEmergency: boolean;
  createRemediationPlan: (params: any) => Promise<any>;
  onProgress?: (update: RemediationItemProgress) => void;
}) {
  let created = 0;

  for (const draft of params.drafts) {
    const progress = { key: draft.key, title: draft.planName };
    params.onProgress?.({ ...progress, stage: 'processing', message: `Creating plan for ${pluralizeDevices(draft.deviceIds.length)}…` });

    try {
      const response = await params.createRemediationPlan({
        remediationPlanPatchList: [toPlanPatch(draft)],
        targetDeviceIds: draft.deviceIds,
        remediationPlanData: {
          PlanName: draft.planName,
          IsEmergencyRemediationPlan: params.isEmergency,
          SendEmailOnCompletion: false,
          RemediationPlanStartTime: new Date().toISOString(),
          IsActive: true,
        },
        silent: true,
        tenantId: params.tenantId,
      });

      if (response) {
        created += 1;
        params.onProgress?.({ ...progress, stage: 'success', message: 'Plan created and approved' });
      } else {
        params.onProgress?.({ ...progress, stage: 'failed', message: "Couldn't create this plan" });
      }
    } catch (error) {
      console.error(`Error creating remediation plan ${draft.planName}:`, error);
      params.onProgress?.({ ...progress, stage: 'failed', message: "Couldn't create this plan" });
    }
  }

  return { created, failed: params.drafts.length - created };
}
